import { PageSkeleton } from '@app/components/page/header/skeleton/PageSkeleton';
import { StationCtxProvider } from '@app/core/containers/StationCtx';
import { Route, useRouteMatch } from 'react-router-dom';
import { PageLoader } from '../../../../components/page/header/skeleton/PageLoader';
import { Page } from '../../../../components/page/Page';
import { useAllowFragmentExternalExport } from '../../hooks/useAllowFragmentExternalExport';
import { FragmentContentAiView } from './components/content-ai/FragmentContentAiView';
import { FragmentFilesView } from './components/export/FragmentExportFilesView';
import { FragmentPublishView } from './components/export/FragmentExportPublishView';
import { FragmentExternalExportView } from './components/export/FragmentExternalExportView';
import { FragmentVideoEditionsTab } from './components/fragment-video-editions-tab/FragmentVideoEditionsTab';
import { FragmentsViewHeader } from './components/FragmentsViewHeader';
import { FragmentTabAccessGuard } from './components/FragmentTabAccessGuard';
import { FragmentViewContentSkeleton } from './components/FragmentViewContentSkeleton';
import { FragmentViewPreload } from './components/FragmentViewPreload';
import { FragmentMediaDetail } from './components/media-detail/FragmentMediaDetail';
import { useFragmentCanPublish } from './hooks/useFragmentCanPublish';
import { usePreviewFragment } from './hooks/usePreviewFragment';

/**
 * Fragment detail layout: header, tab routes (info, files, publish, edits, Content AI).
 * Fragment record is provided by {@link RecordCtxProvider} in {@link FragmentsViewPage}.
 * Tab routes that depend on media/transcription are gated so deep links match disabled-tab UX.
 * Edits requires media only (not transcription).
 * Wraps content in {@link StationCtxProvider} so date/time UI uses the fragment station timezone.
 */
export function FragmentsViewPage() {
  const { query, fragment } = usePreviewFragment();
  const { path } = useRouteMatch();
  const { canPublish, station } = useFragmentCanPublish(fragment);
  const allowExternalExport = useAllowFragmentExternalExport(station);

  const pageContent = (
    <Page htmlTitle={fragment?.title}>
      <FragmentsViewHeader />
      <Page.Content>
        <FragmentViewPreload />
        <Route
          path={`${path}/content-ai`}
          render={() => (
            <FragmentTabAccessGuard requirement="transcription">
              <FragmentContentAiView />
            </FragmentTabAccessGuard>
          )}
        />
        <Route
          path={`${path}/files`}
          render={() => (
            <FragmentTabAccessGuard requirement="media">
              <FragmentFilesView />
            </FragmentTabAccessGuard>
          )}
        />
        {canPublish && <Route path={`${path}/publish`} render={() => <FragmentPublishView />} />}
        {allowExternalExport && (
          <Route
            path={`${path}/export`}
            render={() => (
              <FragmentTabAccessGuard requirement="media">
                <FragmentExternalExportView />
              </FragmentTabAccessGuard>
            )}
          />
        )}
        <Route
          path={`${path}/edits`}
          render={() => (
            <FragmentTabAccessGuard requirement="media">
              <FragmentVideoEditionsTab />
            </FragmentTabAccessGuard>
          )}
        />
        <Route exact path={path} render={() => <FragmentMediaDetail />} />
      </Page.Content>
    </Page>
  );

  return (
    <PageLoader
      skeleton={
        <PageSkeleton breadcrumbs={2} tabs={4}>
          <FragmentViewContentSkeleton />
        </PageSkeleton>
      }
      query={query}
    >
      {station ? (
        <StationCtxProvider station={station}>{pageContent}</StationCtxProvider>
      ) : (
        pageContent
      )}
    </PageLoader>
  );
}
